import React, { useEffect, useRef, useState } from "react";
import "../Style/Whoweare.css";

export default function WhoWeAre() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -100px 0px" }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} className="wwa-section">
      {/* SECTION HEADER — mirrors the reference's title + top-right link */}
      <div className="wwa-header">
        <h2 className="wwa-header-title">Who We Are</h2>
        <a href="#wwa-timeline" className="wwa-header-link">
          OUR JOURNEY
        </a>
      </div>

      {/* SPLIT ROW — full-bleed visual on the left, overlapping glass card on the right */}
      <div className="wwa-cards-row">
        <div className={`wwa-card-left ${isVisible ? "animate" : ""}`}>
          <div className="wwa-building-stage">
            <div className="wwa-building-3d">
              <svg
                viewBox="0 0 400 460"
                className="wwa-building-svg"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="235" y="140" width="70" height="260" rx="4" fill="#0A3A7A" opacity="0.55" />
                <rect x="245" y="160" width="14" height="14" fill="#9EC3FF" opacity="0.5" />
                <rect x="270" y="160" width="14" height="14" fill="#9EC3FF" opacity="0.5" />
                <rect x="245" y="184" width="14" height="14" fill="#9EC3FF" opacity="0.5" />
                <rect x="270" y="184" width="14" height="14" fill="#9EC3FF" opacity="0.5" />
                <rect x="245" y="208" width="14" height="14" fill="#9EC3FF" opacity="0.5" />
                <rect x="270" y="208" width="14" height="14" fill="#9EC3FF" opacity="0.5" />

                <rect x="60" y="260" width="90" height="140" rx="4" fill="#0F4C93" />
                <rect x="76" y="278" width="16" height="16" fill="#DCEBFF" />
                <rect x="102" y="278" width="16" height="16" fill="#DCEBFF" />
                <rect x="128" y="278" width="16" height="16" fill="#DCEBFF" />
                <rect x="76" y="304" width="16" height="16" fill="#DCEBFF" />
                <rect x="102" y="304" width="16" height="16" fill="#DCEBFF" />
                <rect x="128" y="304" width="16" height="16" fill="#DCEBFF" />
                <rect x="76" y="330" width="16" height="16" fill="#DCEBFF" />
                <rect x="102" y="330" width="16" height="16" fill="#DCEBFF" />
                <rect x="128" y="330" width="16" height="16" fill="#DCEBFF" />

                <rect x="155" y="70" width="120" height="330" rx="6" fill="#072E6D" />
                <rect x="155" y="70" width="120" height="330" rx="6" fill="url(#wwaTowerSheen)" />

                {Array.from({ length: 8 }).map((_, row) =>
                  Array.from({ length: 4 }).map((_, col) => (
                    <rect
                      key={`w-${row}-${col}`}
                      x={170 + col * 25}
                      y={92 + row * 35}
                      width="14"
                      height="20"
                      rx="1.5"
                      fill="#168FC0"
                      opacity={0.55 + ((row + col) % 3) * 0.12}
                    />
                  ))
                )}

                <rect x="185" y="46" width="60" height="26" rx="3" fill="#072E6D" />
                <line x1="215" y1="46" x2="215" y2="18" stroke="#9EC3FF" strokeWidth="3" />
                <circle cx="215" cy="14" r="6" fill="#DCEBFF" className="wwa-beacon" />

                <ellipse cx="215" cy="404" rx="150" ry="14" fill="#072E6D" opacity="0.18" />

                <defs>
                  <linearGradient id="wwaTowerSheen" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#9EC3FF" stopOpacity="0.25" />
                    <stop offset="45%" stopColor="#9EC3FF" stopOpacity="0" />
                    <stop offset="100%" stopColor="#001238" stopOpacity="0.15" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="wwa-building-shadow" />

            <div className="wwa-figures" aria-hidden="true">
              {[
                { light: "#9EC3FF", dark: "#0F4C93" },
                { light: "#DCEBFF", dark: "#168FC0" },
                { light: "#7FB8E8", dark: "#0A4A8C" },
              ].map((c, i) => (
                <div className="wwa-figure" key={i}>
                  <svg viewBox="0 0 120 220" className="wwa-person-svg">
                    <defs>
                      <linearGradient id={`personGrad-${i}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={c.light} />
                        <stop offset="100%" stopColor={c.dark} />
                      </linearGradient>
                    </defs>
                    <rect x="38" y="142" width="17" height="56" rx="8.5" fill={c.dark} transform="rotate(-20 46.5 142)" />
                    <rect x="65" y="142" width="17" height="56" rx="8.5" fill={c.dark} transform="rotate(20 73.5 142)" />
                    <rect x="33" y="68" width="54" height="78" rx="24" fill={`url(#personGrad-${i})`} />
                    <rect x="12" y="38" width="17" height="62" rx="8.5" fill={c.dark} transform="rotate(-38 20.5 100)" />
                    <rect x="91" y="38" width="17" height="62" rx="8.5" fill={c.dark} transform="rotate(38 99.5 100)" />
                    <circle cx="60" cy="42" r="25" fill={c.light} />
                    <circle cx="51" cy="35" r="6" fill="#ffffff" opacity="0.5" />
                  </svg>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* overlapping glass card — same anatomy as the reference: eyebrow, heading, quote mark, copy, link */}
        <div className={`wwa-card-right ${isVisible ? "animate" : ""}`}>
          <span className="wwa-eyebrow">MPeoples Business Solutions</span>

          <h3 className="wwa-card-heading">
            Building Digital Solutions for Growing Businesses
          </h3>

          <span className="wwa-quote-mark" aria-hidden="true">&#8221;</span>

          <p className="wwa-card-para">
            MPeoples helps startups and enterprises transform ideas into
            powerful digital products. From UX strategy to final deployment,
            we focus on clarity, performance, and long-term business growth.
          </p>

          {/* <a href="#wwa-timeline" className="wwa-card-link">
            Read Our Story
          </a> */}
        </div>
      </div>

      {/* TIMELINE */}
      <div className="wwa-timeline-wrapper" id="wwa-timeline">
        <div className={`wwa-timeline ${isVisible ? "animate" : ""}`}>
          <div className="wwa-timeline-line"></div>

          <div className="wwa-timeline-item wwa-top-item">
            <span className="wwa-year">2023</span>
            <div className="wwa-dot"></div>
            <p className="wwa-label">Start</p>
          </div>

          <div className="wwa-timeline-item wwa-bottom-item">
            <span className="wwa-year">2024</span>
            <div className="wwa-dot"></div>
            <p className="wwa-label">Foundation &amp; Vision</p>
          </div>

          <div className="wwa-timeline-item wwa-top-item">
            <span className="wwa-year">2025</span>
            <div className="wwa-dot"></div>
            <p className="wwa-label">Product Launch &amp; Market Entry</p>
          </div>

          <div className="wwa-timeline-item wwa-bottom-item">
            <span className="wwa-year">2026</span>
            <div className="wwa-dot"></div>
            <p className="wwa-label">Growth &amp; Innovation</p>
          </div>
        </div>
      </div>
    </section>
  );
}